// Lib
import { memo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { SetRedirectControl } from '@actions/redirectControl';
import { AccountDataLoad } from '@actions/account';
import { UserLogout } from '@actions/user';
import { AccountLogout } from '@actions/account';

// Types
import type { RootState } from '@reducers/index';

const listPath = [
  '/onboarding',
  '/checkout',
  '/campaigns',
  '/saved-creators/lists',
  '/saved-creators',
  '/search',
];

const AppSetup = memo(function AppSetup(): null {
  const dispatch = useDispatch();
  const {
    accountId,
    isLoggedIn,
    username,
    isOnboardingPassed,
    redirectControl,
  } = useSelector((state: RootState) => ({
    accountId: state.Account._id,
    isLoggedIn: !!state.Account._id,
    username: state.User.username,
    isOnboardingPassed:
      !!state?.Account?.profile?.onboardingPassed &&
      state?.Account?.organizations &&
      state?.Account?.organizations?.length,
    redirectControl: state.redirectControl,
    popupId: state.Popup.id,
    isLoading: state.isLoading,
  }));

  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const root = document.getElementById('root');
    const splashLogo = document.getElementById('splash-logo');
    const splashSpinner = document.getElementById('splash-spinner');
    // React has mounted: show the app's own loading states now. Waiting for
    // window.load also waits for unrelated images and third-party scripts,
    // and misses the event entirely when this effect mounts after it fired.
    if (root) root.style.visibility = 'unset';
    if (splashLogo) splashLogo.style.display = 'none';
    if (splashSpinner) splashSpinner.style.display = 'none';
  }, []);

  // Tracks the account `_id` we've already requested data for in this login
  // session. AppSetup is mounted once at the app root and `useSelector` returns
  // a fresh object literal on every store change, so this effect can be
  // evaluated many times per session (and twice on mount under StrictMode).
  // Without this guard each evaluation re-dispatched AccountDataLoad, and since
  // GetAccountData is a `no-cache` query every dispatch became a network
  // round-trip — the source of the excessive refetches. We load account data
  // once per distinct logged-in account; logout resets `_id` so the next login
  // (a genuinely new session) loads again.
  const loadedAccountId = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      loadedAccountId.current = null;
      return;
    }

    if (loadedAccountId.current === accountId) return;

    if (
      pathname?.split('/')[1] === username ||
      listPath.includes(pathname) ||
      pathname.includes('settings')
    ) {
      loadedAccountId.current = accountId;
      dispatch(AccountDataLoad());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, accountId, dispatch]);

  useEffect(() => {
    const version =
      typeof localStorage === 'object' && localStorage.getItem('version');
    if (version !== '3.30' && isLoggedIn) {
      if (typeof localStorage === 'object') {
        // localStorage.removeItem('persist:root');
        // localStorage.clear();
        dispatch(UserLogout());
        dispatch(AccountLogout());
        localStorage.setItem('version', '3.30');
      }
    } else {
      localStorage.setItem('version', '3.30');
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (
      isLoggedIn &&
      !isOnboardingPassed &&
      !pathname.includes('/auth/') &&
      !pathname.includes('/subscription') &&
      !pathname.includes('/checkout') &&
      typeof localStorage === 'object'
    ) {
      navigate('/onboarding');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboardingPassed, isLoggedIn, navigate]);

  useEffect(() => {
    if (redirectControl) navigate(redirectControl);
    dispatch(SetRedirectControl(null));
  }, [redirectControl, navigate, dispatch]);

  return null;
});

export default AppSetup;
